"use strict";
/**
 * FuelEU Maritime surplus model.
 *
 * Compliance balance follows FuelEU Maritime Annex IV:
 * (target intensity − biofuel intensity) × energy delivered in scope, converted to tCO2e.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.SENSITIVITY_INTENSITIES = exports.calculate = exports.toInputs = exports.toNumber = exports.DEFAULT_RAW_INPUTS = exports.DEFAULT_FOSSIL = exports.FOSSIL_FUEL_ORDER = exports.FOSSIL_FUELS = exports.DEFAULT_POOL_PRICE_EUR = exports.PENALTY_EUR_PER_TONNE = exports.CONTACT_EMAIL = exports.FALLBACK_FX = exports.FALLBACK_QUOTE_DATE = exports.BIOFUEL_LCV = exports.TARGET_INTENSITY = void 0;
exports.TARGET_INTENSITY = 89.3368; // FuelEU 2026 target, gCO2e/MJ
exports.BIOFUEL_LCV = 37; // biofuel energy content, MJ/kg
exports.FALLBACK_QUOTE_DATE = "18 Aug 2026";
exports.FALLBACK_FX = 1.1573;
exports.CONTACT_EMAIL = "info@ahticlimate.com";
/**
 * Assumed cost of a FuelEU compliance deficit, EUR per tCO2e. The surplus is
 * valued at this rate throughout: every tonne generated is a tonne of penalty
 * that does not have to be paid.
 */
exports.PENALTY_EUR_PER_TONNE = 640;
/** Default price paid for surplus units in a pool, EUR per tCO2e. */
exports.DEFAULT_POOL_PRICE_EUR = 80;
exports.FOSSIL_FUELS = {
    hfo: { key: "hfo", name: "HFO / VLSFO", ci: 91.7, ef: 3.114, price: 520 },
    lfo: { key: "lfo", name: "LFO", ci: 91.4, ef: 3.151, price: 620 },
    mgo: { key: "mgo", name: "MDO / MGO", ci: 90.77, ef: 3.206, price: 720 },
};
exports.FOSSIL_FUEL_ORDER = ["hfo", "lfo", "mgo"];
exports.DEFAULT_FOSSIL = "mgo";
exports.DEFAULT_RAW_INPUTS = {
    tons: "1000",
    ciBio: "15",
    priceFossil: String(exports.FOSSIL_FUELS[exports.DEFAULT_FOSSIL].price),
    priceBio: "1275",
    eua: "81.35",
    poolPriceEur: String(exports.DEFAULT_POOL_PRICE_EUR),
    fx: String(exports.FALLBACK_FX),
};
const toNumber = (value) => {
    const parsed = parseFloat(value);
    return isFinite(parsed) ? parsed : 0;
};
exports.toNumber = toNumber;
const toInputs = (fossil, raw) => ({
    fossil,
    tons: (0, exports.toNumber)(raw.tons),
    ciBio: (0, exports.toNumber)(raw.ciBio),
    priceFossil: (0, exports.toNumber)(raw.priceFossil),
    priceBio: (0, exports.toNumber)(raw.priceBio),
    eua: (0, exports.toNumber)(raw.eua),
    poolPriceEur: (0, exports.toNumber)(raw.poolPriceEur),
    fx: (0, exports.toNumber)(raw.fx),
});
exports.toInputs = toInputs;
/** Runs the model, optionally overriding the biofuel intensity (used by the sensitivity strip). */
const calculate = (inputs, ciBioOverride) => {
    const ciBio = ciBioOverride ?? inputs.ciBio;
    const fuel = exports.FOSSIL_FUELS[inputs.fossil] ?? exports.FOSSIL_FUELS.mgo;
    const fx = inputs.fx || exports.FALLBACK_FX;
    const megajoules = inputs.tons * exports.BIOFUEL_LCV * 1000;
    // tCO2e, all energy assumed to be in scope
    const surplus = ((exports.TARGET_INTENSITY - ciBio) * megajoules) / 1e6;
    const penaltyRate = exports.PENALTY_EUR_PER_TONNE * fx;
    const poolRate = inputs.poolPriceEur * fx;
    const available = Math.max(surplus, 0);
    const penaltyAvoided = available * penaltyRate;
    const poolProfit = available * poolRate;
    const ets = inputs.tons * fuel.ef * inputs.eua * fx;
    const premium = inputs.tons * (inputs.priceBio - inputs.priceFossil);
    const offsetValue = penaltyAvoided + ets;
    const poolValue = poolProfit + ets;
    return {
        tons: inputs.tons,
        fx,
        fuel,
        surplus,
        penaltyRate,
        penaltyAvoided,
        poolRate,
        poolProfit,
        ets,
        premium,
        offsetValue,
        offsetNet: offsetValue - premium,
        poolValue,
        poolNet: poolValue - premium,
    };
};
exports.calculate = calculate;
exports.SENSITIVITY_INTENSITIES = [5, 10, 15, 20, 30];
